import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { processWriterCompileQueue } from '@/lib/writer/compileQueue';

interface JobRow {
  id: string;
  version_id: string;
  status: string;
  attempts: number;
  enqueued_at: string;
  error: string | null;
}

/**
 * Admin dev harness: shows the current writer_compile_jobs queue owned by the
 * caller and offers a one-click drain while the Tectonic worker isn't yet
 * wired. Non-invasive: no side effects on data other than the queue itself.
 */
const WriterCompileQueuePanel: React.FC = () => {
  const [rows, setRows] = useState<JobRow[] | null>(null);
  const [draining, setDraining] = useState(false);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('writer_compile_jobs')
      .select('id, version_id, status, attempts, enqueued_at, error')
      .order('enqueued_at', { ascending: false })
      .limit(25);
    setRows((data ?? []) as unknown as JobRow[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDrain = useCallback(async () => {
    setDraining(true);
    try {
      const res = await processWriterCompileQueue(5);
      toast.success(
        res.processed.length === 0
          ? 'Queue is empty'
          : `Processed ${res.processed.length} job${res.processed.length === 1 ? '' : 's'}`,
      );
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Drain failed');
    } finally {
      setDraining(false);
    }
  }, [load]);

  const queued = (rows ?? []).filter((r) => r.status === 'queued').length;
  const running = (rows ?? []).filter((r) => r.status === 'running').length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Compile queue</CardTitle>
        <Button size="sm" variant="outline" onClick={handleDrain} disabled={draining}>
          {draining ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Drain queue'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex gap-6 text-xs text-muted-foreground">
          <span>
            Queued: <span className="font-mono text-foreground">{queued}</span>
          </span>
          <span>
            Running: <span className="font-mono text-foreground">{running}</span>
          </span>
        </div>
        {rows === null ? (
          <p className="text-muted-foreground text-xs">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-xs">No compile jobs yet.</p>
        ) : (
          <ul className="space-y-1 text-xs font-mono">
            {rows.slice(0, 10).map((r) => (
              <li
                key={r.id}
                className="flex justify-between gap-3 border-b border-border/40 py-1"
              >
                <span className="truncate">{r.version_id.slice(0, 8)}…</span>
                <span
                  className={
                    r.status === 'failed'
                      ? 'text-destructive'
                      : r.status === 'success'
                        ? 'text-emerald-600'
                        : 'text-muted-foreground'
                  }
                >
                  {r.status}
                </span>
                <span className="text-muted-foreground">×{r.attempts}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-foreground pt-1">
          The Tectonic build worker is not yet configured. Draining currently
          re-queues jobs and records a stub log — real PDFs appear once
          `WRITER_COMPILE_WORKER_URL` is set.
        </p>
      </CardContent>
    </Card>
  );
};

export default WriterCompileQueuePanel;
